<script lang="ts">
  import { api, ApiError, OfflineError } from '../lib/api'
  import { ripple } from '../lib/ripple'
  import Notice from '../lib/components/Notice.svelte'
  import Loading from '../lib/components/Loading.svelte'

  /*
   * The landing page for the link in a password-reset email.
   *
   * Two things this page does NOT do, both deliberate. It does not decrypt the token — that is the
   * account estate's, and a second redeemer is how you get a second implementation of a token scheme
   * (see docs/ACCOUNT-API.md, and `platform-architecture` §13.7). And it does not decide whether a
   * password is acceptable: the API's 422 is the authority, and the rule below is mirrored only so
   * somebody is told before they submit rather than after.
   */

  // The Website's rule, from HomeController: at least six characters, one lowercase, one uppercase, one
  // digit. Mirrored here for live feedback; if the API tightens it, the 422 still wins.
  const STRONG = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/
  const RULE = 'Passwords must be at least six characters and contain a lowercase letter, an uppercase letter and a number.'

  const token = new URLSearchParams(location.search).get('token') ?? ''

  let password = $state('')
  let confirm = $state('')
  let busy = $state(false)
  let done = $state(false)
  let submitted = $state(false)
  let error = $state<string | null>(null)

  const mismatch = $derived(submitted && confirm !== '' && password !== confirm)
  const tooWeak = $derived(submitted && password !== '' && !STRONG.test(password))

  // Started once, at construction: the token cannot change while the page is open, so re-running this in
  // an effect could only re-ask the same question and burn a redemption attempt doing it.
  const target = token ? api.passwordResetTarget(token) : null

  async function submit(event: SubmitEvent) {
    event.preventDefault()
    submitted = true
    if (busy) return
    if (!password || password !== confirm || !STRONG.test(password)) return

    busy = true
    error = null
    try {
      await api.resetPassword(token, password)
      done = true
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) error = 'expired'
      else if (e instanceof ApiError && e.status === 422) error = e.detail ?? RULE
      else if (e instanceof OfflineError) error = 'Could not reach Bird Journal. Check your connection and try again.'
      else error = 'Something went wrong. Please try again.'
    } finally {
      busy = false
    }
  }
</script>

{#if done}
  <Notice tone="ok" live>Your password has been changed.</Notice>
  <p>You can now sign in with your new password, here and in the Bird Journal apps.</p>
  <a class="btn btn-accent rip submit" use:ripple href="/sign-in">Sign in</a>
{:else if !token}
  <Notice tone="error">This link is not a valid password reset link.</Notice>
  <p>Check you copied the whole link from the email, or request a new one.</p>
  <a class="btn btn-tonal rip submit" use:ripple href="/forgot-password">Request a new link</a>
{:else}
  {#await target}
    <Loading rows={2} />
  {:then result}
    {#if error === 'expired'}
      <Notice tone="error">This password reset link has expired.</Notice>
      <a class="btn btn-tonal rip submit" use:ripple href="/forgot-password">Request a new link</a>
    {:else}
      {#if error}
        <Notice tone="error">{error}</Notice>
      {/if}

      <p>Choose a new password for <strong>{result!.email}</strong>.</p>

      <form onsubmit={submit} novalidate>
        <div class="field">
          <label class="field-label" for="password">New password</label>
          <div class="sfield" class:invalid={tooWeak}>
            <input id="password" type="password" bind:value={password} autocomplete="new-password" required />
          </div>
          {#if tooWeak}
            <span class="field-error">{RULE}</span>
          {:else}
            <span class="hint">{RULE}</span>
          {/if}
        </div>

        <div class="field">
          <label class="field-label" for="confirm">Confirm new password</label>
          <div class="sfield" class:invalid={mismatch}>
            <input id="confirm" type="password" bind:value={confirm} autocomplete="new-password" required />
          </div>
          {#if mismatch}
            <span class="field-error">The passwords entered are not the same.</span>
          {/if}
        </div>

        <button class="btn btn-accent rip submit" use:ripple type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Change my password'}
        </button>
      </form>
    {/if}
  {:catch e}
    {#if e instanceof ApiError && e.status === 410}
      <Notice tone="error">This password reset link has expired.</Notice>
    {:else}
      <Notice tone="error">This link is not a valid password reset link.</Notice>
    {/if}
    <a class="btn btn-tonal rip submit" use:ripple href="/forgot-password">Request a new link</a>
  {/await}
{/if}

<style>
  .submit {
    width: 100%;
    margin-top: var(--space-5);
  }
  .hint {
    display: block;
    margin-top: var(--space-2);
    font-size: 0.85rem;
    color: var(--bj-on-surface-variant);
  }
</style>
